import React, { useState, useMemo, useCallback } from "react";
import { Collapse } from "react-collapse";
import SearchBar from "../../atoms/searchBar/SearchBar.jsx";
import EventCard from "../eventCard/EventCard.jsx";
import Loader from "../../atoms/loader/Loader.jsx";
import "./UserHistoricEventsPage.css";

const UserHistoricEventsPage = ({
  openSection,
  isSmallScreen,
  userHistoricEventsList,
  handleEventSelect,
  loadingEventId,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const convertToHectares = useCallback((areaM2) => {
    if (areaM2 === null || areaM2 === undefined) return "0";
    const hectares = areaM2 / 10000;
    return hectares.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      .replace(",", " à");
  }, []);

  const formatDateShort = useCallback((dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }, []);

  const filteredData = useMemo(() => {
    if (!Array.isArray(userHistoricEventsList.data)) return [];

    // Sort by created_at descending (most recent first)
    const sorted = [...userHistoricEventsList.data].sort((a, b) => {
      const dateA = new Date(a.fire.created_at ?? 0);
      const dateB = new Date(b.fire.created_at ?? 0);
      return dateB - dateA;
    });

    if (!searchQuery.trim()) return sorted;

    const q = searchQuery.toLowerCase().trim();

    const getSearchableType = (backendType) => {
      if (!backendType) return "";
      const lower = backendType.toLowerCase();
      const map = { dynamic: "satellite" };
      const mapped = map[lower] || lower;
      return `${lower} ${mapped}`;
    };

    return sorted.filter((item) => {
      const fire = item.fire;
      const searchableType = getSearchableType(fire.type);

      return (
        (fire.reference && fire.reference.toLowerCase().includes(q)) ||
        (fire.address && fire.address.toLowerCase().includes(q)) ||
        (searchableType && searchableType.includes(q)) ||
        (fire.date && fire.date.toLowerCase().includes(q)) ||
        (fire.date && formatDateShort(fire.date).toLowerCase().includes(q))
      );
    });
  }, [searchQuery, userHistoricEventsList.data, formatDateShort]);

  const eventListJSX = (
    <div
      className="events-list-container"
      data-testid="historic-events-list-container"
    >
      <SearchBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        resultCount={filteredData.length}
        placeholder="Rechercher par référence, adresse, date, type..."
        data-testid="historic-events-search-bar"
      />
      <div
        className="events-scrollable"
        data-testid="historic-events-scrollable"
      >
        {filteredData.length === 0 && searchQuery ? (
          <div className="no-results" data-testid="historic-events-no-results">
            <p>Aucun événement trouvé pour « {searchQuery} »</p>
          </div>
        ) : (
          filteredData.map((item, index) => (
            <EventCard
              key={item.fire.id ?? index}
              data-testid={`historic-event-card`}
              item={item}
              isSelected={
                userHistoricEventsList.selectedEventId === item.fire.id
              }
              isLoading={loadingEventId === item.fire.id}
              onSelect={handleEventSelect}
              formatDate={formatDate}
              formatDateShort={formatDateShort}
              convertToHectares={convertToHectares}
            />
          ))
        )}
      </div>
    </div>
  );

  return (
    <>
      {isSmallScreen ? (
        <Collapse isOpened={openSection === "eventsHistoric"}>
          {userHistoricEventsList.isLoading ? (
            <Loader size={10} />
          ) : (
            eventListJSX
          )}
        </Collapse>
      ) : (
        <div
          className="user-historic-events-page"
          data-testid="historic-events-desktop-container"
        >
          {userHistoricEventsList.isLoading ? (
            <Loader size={20} />
          ) : (
            eventListJSX
          )}
        </div>
      )}
    </>
  );
};

export default UserHistoricEventsPage;
